// Wave AW.3-React (2026-05-16) — 외부 바이어 경매 목록 화면
// Wave UX-2.1 Item 1 sweep (2026-05-23) — 4 locale i18n.
// Wave UX-2.1 Item 3 (2026-05-23) — 필터 chip 4종 (year/mileage/price/inspected) 클라이언트 필터.
//   - 백엔드 변경 0 (현재 페이지의 결과 안에서 클라이언트 AND 필터).
//   - 단일 선택 per 그룹 + AND 조합.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized, displayApiError } from './buyerApi';
import AuctionBuyerCard from './AuctionBuyerCard';
import AuctionBuyerLayout from './AuctionBuyerLayout';
import EmptyState from './EmptyState';
import { useT } from '../../i18n/I18nProvider';

var SORT_OPTIONS = [
  { key: 'ending_soon', labelKey: 'sortEndingSoon' },
  { key: 'newest',      labelKey: 'sortNewest' },
  { key: 'price_asc',   labelKey: 'sortPriceLow' },
  { key: 'price_desc',  labelKey: 'sortPriceHigh' },
];

// 필터 옵션 정의 (Flutter Wave UX-1 Item 1.6 정합)
var YEAR_OPTIONS = [
  { key: 'all',          labelKey: 'yearFilterAll',       test: function() { return true; } },
  { key: 'y2020plus',    labelKey: 'yearFilter2020Plus',  test: function(y) { return y >= 2020; } },
  { key: 'y2015to2019',  labelKey: 'yearFilter2015to2019',test: function(y) { return y >= 2015 && y <= 2019; } },
  { key: 'y2014minus',   labelKey: 'yearFilter2014Minus', test: function(y) { return y > 0 && y <= 2014; } },
];

var MILEAGE_OPTIONS = [
  { key: 'all',       labelKey: 'mileageFilterAll', test: function() { return true; } },
  { key: 'under5',    labelKey: 'mileageUnder5',    test: function(km) { return km < 50000; } },
  { key: 'from5to10', labelKey: 'mileage5to10',     test: function(km) { return km >= 50000 && km < 100000; } },
  { key: 'over10',    labelKey: 'mileageOver10',    test: function(km) { return km >= 100000; } },
];

var PRICE_OPTIONS = [
  { key: 'all',       labelKey: 'priceFilterAll', test: function() { return true; } },
  { key: 'under1k',   labelKey: 'priceUnder1k',   test: function(p) { return p < 10000000; } },
  { key: 'from1to3k', labelKey: 'price1to3k',     test: function(p) { return p >= 10000000 && p < 30000000; } },
  { key: 'from3to5k', labelKey: 'price3to5k',     test: function(p) { return p >= 30000000 && p < 50000000; } },
  { key: 'over5k',    labelKey: 'priceOver5k',    test: function(p) { return p >= 50000000; } },
];

function findOpt(arr, key) { return arr.find(function(o){ return o.key === key; }) || arr[0]; }

function AuctionBuyerAuctionList() {
  var navigate = useNavigate();
  var t = useT();
  var [items, setItems] = useState([]);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var [page, setPage] = useState(1);
  var [totalPages, setTotalPages] = useState(1);
  var [sort, setSort] = useState('ending_soon');
  var [q, setQ] = useState('');           // 입력 immediate state
  // Wave UX-2.3 #15 — 300ms 디바운스 (load 가 debouncedQ 의존)
  var [debouncedQ, setDebouncedQ] = useState('');
  React.useEffect(function() {
    var h = setTimeout(function(){ setDebouncedQ(q); setPage(1); }, 300);
    return function(){ clearTimeout(h); };
  }, [q]);

  // Wave UX-2.1 Item 3 — 클라이언트 필터 state (모두 'all' 기본)
  var [yearKey, setYearKey] = useState('all');
  var [mileageKey, setMileageKey] = useState('all');
  var [priceKey, setPriceKey] = useState('all');
  var [inspectedOnly, setInspectedOnly] = useState(false);

  var load = useCallback(function() {
    setLoading(true);
    setError('');
    var params = new URLSearchParams({
      status: 'active',
      sort: sort,
      page: String(page),
      limit: '20',
    });
    if (debouncedQ.trim()) params.append('q', debouncedQ.trim());

    authBuyerFetch(API + '/buyer-app/auction/list?' + params.toString(), {})
      .then(function(r) {
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json();
      })
      .then(function(d) {
        if (d && d.success && d.data) {
          setItems(d.data.auctions || []);
          setTotalPages((d.data.pagination && d.data.pagination.totalPages) || 1);
        } else {
          setError(displayApiError(d, t, 'errListLoad'));
        }
        setLoading(false);
      })
      .catch(function(e) {
        if (e && e.message === 'unauthorized') return;
        setError(t('commonNetwork'));
        setLoading(false);
      });
  }, [sort, page, debouncedQ, navigate, t]);

  useEffect(function() { load(); }, [load]);

  var handleSearch = function() { setPage(1); load(); };

  // 클라이언트 AND 필터 — 현재 page 결과 안에서.
  var yearOpt = findOpt(YEAR_OPTIONS, yearKey);
  var mileageOpt = findOpt(MILEAGE_OPTIONS, mileageKey);
  var priceOpt = findOpt(PRICE_OPTIONS, priceKey);
  var filtered = items.filter(function(a) {
    var car = a.car || {};
    var stats = a.liveStats || {};
    var year = parseInt(car.year, 10) || 0;
    var km = (typeof car.mileage === 'number') ? car.mileage : (parseInt(car.mileage, 10) || 0);
    var price = (typeof a.highestBid === 'number' && a.highestBid > 0) ? a.highestBid : (stats.highestBid || 0);
    if (!yearOpt.test(year)) return false;
    if (!mileageOpt.test(km)) return false;
    if (!priceOpt.test(price)) return false;
    if (inspectedOnly && !(a.hasInspection === true || (a.inspection && a.inspection.has === true))) return false;
    return true;
  });

  var anyFilterActive = yearKey !== 'all' || mileageKey !== 'all' || priceKey !== 'all' || inspectedOnly;

  function resetFilters() {
    setYearKey('all');
    setMileageKey('all');
    setPriceKey('all');
    setInspectedOnly(false);
  }

  function selectStyle(active) {
    return {
      height: 32, padding: '0 10px',
      border: '1.5px solid ' + (active ? '#1B3A5C' : '#e2e8f0'),
      borderRadius: 999,
      background: active ? '#1B3A5C' : 'white',
      color: active ? 'white' : '#475569',
      fontSize: 12, fontWeight: 700,      cursor: 'pointer', outline: 'none',
    };
  }

  function toggleStyle(active) {
    return Object.assign({}, selectStyle(active), {
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '0 12px',
    });
  }

  return (
    <AuctionBuyerLayout activeKey="auctions">
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 32px 80px' }}>
        {/* 헤더 + 정렬/검색 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{t('auctionsTitle')}</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select value={sort} onChange={function(e){setSort(e.target.value); setPage(1);}}
              style={{
                height: 36, padding: '0 12px',
                border: '1.5px solid #e2e8f0', borderRadius: 8,
                background: 'white', fontSize: 13, fontWeight: 600,
                color: '#475569', cursor: 'pointer',              }}>
              {SORT_OPTIONS.map(function(o){return <option key={o.key} value={o.key}>{t(o.labelKey)}</option>;})}
            </select>
            <div style={{ display: 'flex', gap: 4 }}>
              <input type="text" value={q} onChange={function(e){setQ(e.target.value);}}
                onKeyDown={function(e){if(e.key==='Enter')handleSearch();}}
                placeholder={t('searchPlaceholder')}
                style={{
                  height: 36, padding: '0 14px',
                  border: '1.5px solid #e2e8f0', borderRadius: 8,
                  fontSize: 13, outline: 'none',
                  background: 'white',                  width: 180,
                }}
              />
              <button type="button" onClick={handleSearch}
                style={{
                  height: 36, padding: '0 14px',
                  border: 'none', borderRadius: 8,
                  background: '#1B3A5C', color: 'white',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer',                }}>
                {t('commonSearch')}
              </button>
            </div>
          </div>
        </div>

        {/* Wave UX-2.1 Item 3 — 필터 chip row (year/mileage/price + inspected toggle + reset) */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
          {/* year */}
          <select value={yearKey} onChange={function(e){setYearKey(e.target.value);}}
            style={selectStyle(yearKey !== 'all')} aria-label={t('filterChipYear')}>
            {YEAR_OPTIONS.map(function(o){ return <option key={o.key} value={o.key}>{t(o.labelKey)}</option>; })}
          </select>
          {/* mileage */}
          <select value={mileageKey} onChange={function(e){setMileageKey(e.target.value);}}
            style={selectStyle(mileageKey !== 'all')} aria-label={t('filterChipMileage')}>
            {MILEAGE_OPTIONS.map(function(o){ return <option key={o.key} value={o.key}>{t(o.labelKey)}</option>; })}
          </select>
          {/* price */}
          <select value={priceKey} onChange={function(e){setPriceKey(e.target.value);}}
            style={selectStyle(priceKey !== 'all')} aria-label={t('filterChipPrice')}>
            {PRICE_OPTIONS.map(function(o){ return <option key={o.key} value={o.key}>{t(o.labelKey)}</option>; })}
          </select>
          {/* inspected toggle */}
          <button type="button" onClick={function(){setInspectedOnly(!inspectedOnly);}}
            style={toggleStyle(inspectedOnly)}>
            <span>{inspectedOnly ? '✓' : '○'}</span>
            <span>{t('filterChipInspected')}</span>
          </button>
          {anyFilterActive && (
            <button type="button" onClick={resetFilters}
              style={Object.assign({}, selectStyle(false), { color: '#dc2626', borderColor: '#fecaca' })}>
              {t('filterChipReset')}
            </button>
          )}
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#94a3b8' }}>
            {filtered.length} / {items.length}
          </span>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: 14, marginBottom: 20, color: '#991b1b', fontSize: 14 }}>
            ⚠ {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>{t('auctionsListLoading')}</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon="🔍" title={t('auctionsListEmpty')} hint={t('auctionsListEmptyHint')} />
        ) : (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 16,
            }}>
              {filtered.map(function(auction) {
                return (
                  <AuctionBuyerCard key={auction.id} auction={auction}
                    onClick={function() { navigate('/buyer/auctions/' + auction.id); }}
                  />
                );
              })}
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 32 }}>
                <button type="button" disabled={page <= 1}
                  onClick={function(){setPage(page - 1);}}
                  style={{ ...pageBtnStyle, opacity: page <= 1 ? 0.4 : 1, cursor: page <= 1 ? 'not-allowed' : 'pointer' }}>
                  {t('paginationPrev')}
                </button>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#475569', padding: '0 12px' }}>
                  {page} / {totalPages}
                </span>
                <button type="button" disabled={page >= totalPages}
                  onClick={function(){setPage(page + 1);}}
                  style={{ ...pageBtnStyle, opacity: page >= totalPages ? 0.4 : 1, cursor: page >= totalPages ? 'not-allowed' : 'pointer' }}>
                  {t('paginationNext')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </AuctionBuyerLayout>
  );
}

var pageBtnStyle = {
  height: 36, padding: '0 16px',
  border: '1.5px solid #e2e8f0', borderRadius: 8,
  background: 'white', fontSize: 13, fontWeight: 600,
  color: '#475569',};

export default AuctionBuyerAuctionList;
